import type { PhotoKind } from '../../api/client'

// Photos are resized on her phone before they go up: a cover to at most
// 1600px on its long side, a profile photo cropped to a 480px square. That
// keeps uploads small on mobile data and well under the API's 2 MB limit.
export const COVER_MAX = 1600
export const PROFILE_SIZE = 480

export type Crop = { sx: number; sy: number; sw: number; sh: number; width: number; height: number }

// cropFor is the part of a w×h image to draw, and the size to draw it at.
export function cropFor(kind: PhotoKind, w: number, h: number): Crop {
  if (kind === 'profile') {
    const side = Math.min(w, h)
    const out = Math.min(side, PROFILE_SIZE)
    return { sx: Math.round((w - side) / 2), sy: Math.round((h - side) / 2), sw: side, sh: side, width: out, height: out }
  }
  const scale = Math.min(1, COVER_MAX / Math.max(w, h))
  return { sx: 0, sy: 0, sw: w, sh: h, width: Math.round(w * scale), height: Math.round(h * scale) }
}

// drawAndEncode decodes the file (iPhones hand over HEIC as JPEG through the
// file picker; the browser applies the camera orientation), draws the part
// cropFor picks onto a canvas and encodes it as JPEG. It returns the size it
// drew at, which the note-photo upload sends along.
async function drawAndEncode(file: Blob, kind: PhotoKind): Promise<{ blob: Blob; width: number; height: number }> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const c = cropFor(kind, bitmap.width, bitmap.height)
    const canvas = document.createElement('canvas')
    canvas.width = c.width
    canvas.height = c.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no canvas')
    ctx.drawImage(bitmap, c.sx, c.sy, c.sw, c.sh, 0, 0, c.width, c.height)
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('could not encode'))), 'image/jpeg', 0.85),
    )
    return { blob, width: c.width, height: c.height }
  } finally {
    bitmap.close()
  }
}

// resizePhoto turns a picked file into a JPEG ready to upload.
export async function resizePhoto(file: Blob, kind: PhotoKind): Promise<Blob> {
  return (await drawAndEncode(file, kind)).blob
}

// resizeNotePhoto does the same for a treatment photo: cover sizing, so a
// phone photo lands well under the API's 2 MB limit. A file the browser cannot
// decode rejects with 'unreadable' so the caller can say so.
export async function resizeNotePhoto(file: Blob): Promise<{ blob: Blob; width: number; height: number }> {
  try {
    return await drawAndEncode(file, 'cover')
  } catch {
    throw new Error('unreadable')
  }
}
